import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import s from "./Ombor.module.css";
import {
  listProducts, listWarehouses, listCategories, listBrands, listPriceTypes,
  getStockCards, transferStock
} from "../services/maxsulotlarApi";

/* ================= Decimal helpers ================= */
const normDecStr = (v) => {
  if (v == null) return "";
  let s = String(v).trim().replace(",", ".");
  s = s.replace(/[^\d.-]/g, "");
  if (s.includes("-")) s = (s.startsWith("-") ? "-" : "") + s.replace(/-/g, "");
  const i = s.indexOf(".");
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
  if (["", "-", ".", "-."].includes(s)) return "";
  return s;
};
const toNum = (v) => {
  const n = Number(normDecStr(v));
  return Number.isFinite(n) ? n : 0;
};
const money = (v, { min = 2, max = 2 } = {}) =>
  toNum(v).toLocaleString("uz-UZ", { minimumFractionDigits: min, maximumFractionDigits: max }) + " so'm";
const fmtQty = (v, { min = 0, max = 3 } = {}) =>
  toNum(v).toLocaleString("uz-UZ", { minimumFractionDigits: min, maximumFractionDigits: max });

/* ================= Perf helpers ================= */
const PAGE_SIZE = 120;
const uniqById = (list) => {
  const seen = new Set(); const out = [];
  for (const it of list) { const k = String(it.id); if (!seen.has(k)) { seen.add(k); out.push(it); } }
  return out;
};
const scheduleIdle = (fn) => setTimeout(fn, 0);

/* ================================================== */
export default function OmborPage() {
  /* Reference ro‘yxatlar */
  const [warehouses, setWarehouses] = useState([{ id: "all", nom: "Hamma ombor" }]);
  const [categories, setCategories] = useState([{ id: "all", nom: "Barchasi" }]);
  const [brands, setBrands] = useState([{ id: "all", nom: "Barchasi" }]);
  const [priceTypes, setPriceTypes] = useState([]);

  /* Filtrlar */
  const [filter, setFilter] = useState({ sklad: "all", kategoriya: "all", brand: "all", search: "" });

  /* Qidiruvlar */
  const [qLeft, setQLeft] = useState("");   // chap: server debounced
  const [qRight, setQRight] = useState(""); // o‘ng: basket local

  /* Katalog (infinite) */
  const [catalog, setCatalog] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const loaderRef = useRef(null);
  const fetchingRef = useRef(false);

  /* Stat kartalar */
  const [cards, setCards] = useState({ total_qty: 0, total_cost: 0, total_optom: 0, total_donaga: 0 });

  /* Basket / transfer */
  const [basket, setBasket] = useState([]); // {id,name,sku,qty,unitUsd,rasm_url,prices}
  const [destWh, setDestWh] = useState("");
  const [extraUsd, setExtraUsd] = useState("");
  const [extraPct, setExtraPct] = useState("");
  const [submitting, setSubmitting] = useState(false);

  /* ---------- Init ---------- */
  useEffect(() => {
    (async () => {
      try {
        const [W, C, B, T] = await Promise.all([
          listWarehouses(), listCategories(), listBrands(), listPriceTypes()
        ]);
        setWarehouses([{ id: "all", nom: "Hamma ombor" }, ...W]);
        setCategories([{ id: "all", nom: "Barchasi" }, ...C]);
        setBrands([{ id: "all", nom: "Barchasi" }, ...B]);
        setPriceTypes(T || []);
      } catch (e) {
        console.error("Init refs error", e);
        alert("Ma'lumotlarni yuklashda xato.");
      }
    })();
  }, []);

  /* ---------- Debounce: qLeft -> filter.search ---------- */
  useEffect(() => {
    const id = setTimeout(() => setFilter((f) => ({ ...f, search: qLeft })), 300);
    return () => clearTimeout(id);
  }, [qLeft]);

  /* ---------- Product mapper ---------- */
  const mapProduct = (p) => {
    const prices = {};
    (p.narxlar || []).forEach((n) => { prices[(n.sarxturi || "").toLowerCase()] = Number(n.narx || 0); });
    return {
      id: p.id,
      name: p.nom,
      sku: p.seriyasi,
      qty: Number(p.soni || 0),
      buy: Number(p.tan_narxi || 0),
      rasm_url: p.rasm_url || (p.rasm ? p.rasm : null),
      sklad: p.sklad,
      prices,
    };
  };

  /* ---------- Load first page ---------- */
  const loadFirst = useCallback(async () => {
    setLoading(true);
    try {
      const { rows, next } = await listProducts({
        page: 1, pageSize: PAGE_SIZE,
        sklad: filter.sklad, kategoriya: filter.kategoriya, brand: filter.brand,
        search: filter.search, ordering: "-id",
      });
      scheduleIdle(() => {
        setCatalog(rows.map(mapProduct));
        setPage(1);
        setHasMore(Boolean(next));
        setLoading(false);
      });
    } catch (e) {
      console.error("Catalog load error", e);
      setCatalog([]); setHasMore(false); setLoading(false);
    }
  }, [filter.sklad, filter.kategoriya, filter.brand, filter.search]);

  /* ---------- Load stat cards ---------- */
  const loadCards = useCallback(async () => {
    try {
      const data = await getStockCards({
        sklad: filter.sklad, kategoriya: filter.kategoriya, brand: filter.brand
      });
      setCards({
        total_qty: data.total_qty,
        total_cost: data.total_cost,
        total_optom: data.total_optom,
        total_donaga: data.total_donaga,
      });
    } catch (e) { console.error("Cards error", e); }
  }, [filter.sklad, filter.kategoriya, filter.brand]);

  useEffect(() => { Promise.all([loadFirst(), loadCards()]).catch(() => {}); }, [loadFirst, loadCards]);

  /* ---------- Fetch more (infinite) ---------- */
  const fetchMore = useCallback(async (nextPage) => {
    const { rows, next } = await listProducts({
      page: nextPage, pageSize: PAGE_SIZE,
      sklad: filter.sklad, kategoriya: filter.kategoriya, brand: filter.brand,
      search: filter.search, ordering: "-id",
    });
    scheduleIdle(() => {
      setCatalog((prev) => uniqById([...prev, ...rows.map(mapProduct)]));
      setHasMore(Boolean(next));
    });
  }, [filter.sklad, filter.kategoriya, filter.brand, filter.search]);

  useEffect(() => {
    if (!loaderRef.current) return;
    const node = loaderRef.current;
    const io = new IntersectionObserver(async ([entry]) => {
      if (!entry.isIntersecting) return;
      if (!hasMore || loading) return;
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      const next = page + 1;
      setPage(next);
      try { await fetchMore(next); }
      finally { fetchingRef.current = false; }
    }, { rootMargin: "600px 0px" });
    io.observe(node);
    return () => io.disconnect();
  }, [loaderRef, hasMore, loading, page, fetchMore]);

  /* ---------- Views ---------- */
  const leftView = useMemo(() => catalog, [catalog]);

  const basketView = useMemo(() => {
    const sTerm = (qRight || "").trim().toLowerCase();
    const usd = toNum(extraUsd);
    const pct = toNum(extraPct);
    return basket
      .filter((b) => `${b.name} ${b.sku}`.toLowerCase().includes(sTerm))
      .map((b) => {
        const add = usd + (toNum(b.unitUsd) * pct) / 100;
        const unitAdj = Math.max(0, toNum(b.unitUsd) + add);
        return { ...b, unitAdj };
      });
  }, [basket, qRight, extraUsd, extraPct]);

  const totalQty = basketView.reduce((t, x) => t + toNum(x.qty), 0);
  const totalSum = basketView.reduce((t, x) => t + toNum(x.unitAdj) * toNum(x.qty), 0);

  /* ---------- Actions ---------- */
  const addToBasket = useCallback((p) => {
    if (filter.sklad === "all") { alert("Avval manba omborni tanlang."); return; }
    if (p.qty <= 0) return;
    setBasket((prev) => {
      const i = prev.findIndex((x) => x.id === p.id);
      const unit = (p.prices["donaga"] ?? p.prices["optom"] ?? p.buy ?? 0);
      if (i > -1) {
        const cp = [...prev];
        cp[i] = { ...cp[i], qty: String(toNum(cp[i].qty) + 1) };
        return cp;
      }
      return [...prev, {
        id: p.id, name: p.name, sku: p.sku, qty: "1", unitUsd: String(unit),
        rasm_url: p.rasm_url || null,
      }];
    });
  }, [filter.sklad]);

  const changeQtyDelta = useCallback((id, diff) => {
    setBasket((prev) =>
      prev
        .map((x) => x.id === id ? { ...x, qty: String(Math.max(0, toNum(x.qty) + diff)) } : x)
        .filter((x) => toNum(x.qty) > 0)
    );
  }, []);
  const editQty = useCallback((id, v) => {
    const sVal = normDecStr(v);
    setBasket((prev) => prev.map((x) => (x.id === id ? { ...x, qty: sVal } : x)));
  }, []);
  const editUnit = useCallback((id, v) => {
    const sVal = normDecStr(v);
    setBasket((prev) => prev.map((x) => (x.id === id ? { ...x, unitUsd: sVal } : x)));
  }, []);
  const removeRow = useCallback((id) => setBasket((p) => p.filter((x) => x.id !== id)), []);

  const canTransfer = destWh && basketView.length > 0 && filter.sklad !== "all" && !submitting;

  const doTransfer = useCallback(async () => {
    if (!canTransfer) return;
    try {
      setSubmitting(true);
      const items = basketView.map((b) => ({
        product_id: b.id,
        qty: Number(toNum(b.qty)),
        new_price: toNum(b.unitAdj),
      }));
      const r = await transferStock({
        from_sklad: filter.sklad,
        to_sklad: destWh,
        note: "",
        items,
      });
      alert(`O‘tkazildi ✅ #${r.id}\nJami: ${fmtQty(r.total_qty)} dona, ${money(r.total_value)}`);
      setBasket([]); setExtraUsd(""); setExtraPct(""); setQRight("");
      await Promise.all([loadFirst(), loadCards()]);
    } catch (e) {
      console.error("transfer", e);
      alert(e?.response?.data?.detail || "Transferda xatolik.");
    } finally { setSubmitting(false); }
  }, [canTransfer, basketView, destWh, filter.sklad, loadFirst, loadCards]);

  /* ---------- UI ---------- */
  return (
    <div className={s.wrap}>
      {/* Header stats */}
      <div className={`${s.header} fr-panel`}>
        <div className={s.title}>Ombor nazorati</div>
        <div className={s.stats}>
          <div className={s.stat}><div className={s.num}>{fmtQty(cards.total_qty)}</div><div className={s.lbl}>dona mahsulot</div></div>
          <div className={s.stat}><div className={s.num}>{money(cards.total_cost)}</div><div className={s.lbl}>tan narx qiymati</div></div>
          <div className={s.stat}><div className={s.num}>{money(cards.total_optom)}</div><div className={s.lbl}>optom qiymati</div></div>
          <div className={s.stat}><div className={s.num}>{money(cards.total_donaga)}</div><div className={s.lbl}>donaga qiymati</div></div>
        </div>
      </div>

      {/* Filters */}
      <div className={`${s.filters} fr-panel`}>
        <select className={s.input} value={filter.sklad} onChange={(e)=>setFilter((f)=>({...f, sklad:e.target.value}))}>
          {warehouses.map((w)=> <option key={w.id} value={w.id}>{w.nom || w.name}</option>)}
        </select>

        <select className={s.input} disabled>
          <option>
            Tan narxi + ({priceTypes.map((t)=>t.nom).join(", ") || "narx turlari"})
          </option>
        </select>

        <select className={s.input} value={filter.kategoriya} onChange={(e)=>setFilter((f)=>({...f, kategoriya:e.target.value}))}>
          {categories.map((c)=> <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>

        <select className={s.input} value={filter.brand} onChange={(e)=>setFilter((f)=>({...f, brand:e.target.value}))}>
          {brands.map((b)=> <option key={b.id} value={b.id}>{b.nom}</option>)}
        </select>
      </div>

      {/* Layout */}
      <div className={s.cols}>
        {/* LEFT */}
        <div className={s.left}>
          <div className={`${s.cardScroll} fr-card`}>
            <div className={s.localSearch}>
              <input
                className={s.input}
                placeholder="Mahsulot nomi / seriyasi"
                value={qLeft}
                onChange={(e)=>setQLeft(e.target.value)}
              />
            </div>

            <div className={s.pGrid}>
              {leftView.map((p)=> <ProductCard key={p.id} p={p} onAdd={addToBasket} />)}
            </div>

            {!loading && leftView.length === 0 && <div className={s.empty}>Mahsulot topilmadi</div>}
            {loading && <div className={s.empty}>Yuklanmoqda…</div>}
            <div ref={loaderRef} style={{ height: 1 }} />
          </div>
        </div>

        {/* RIGHT */}
        <div className={s.right}>
          <div className={`${s.panel} fr-card`}>
            <div className={s.rHead}>
              <select className={`${s.input} ${s.sm}`} value={destWh} onChange={(e)=>setDestWh(e.target.value)}>
                <option value="">Qaysi skladga?</option>
                {warehouses
                  .filter((w)=> String(w.id)!=="all" && String(w.id)!==String(filter.sklad))
                  .map((w)=> <option key={w.id} value={w.id}>{w.nom || w.name}</option>)}
              </select>

              <div className={s.miniSearch}>
                <input
                  className={s.input}
                  placeholder="O‘tkazilayotgan tovarlardan qidiring"
                  value={qRight}
                  onChange={(e)=>setQRight(e.target.value)}
                />
                <button className={`${s.glass} ${s.sm}`} aria-label="Search in basket">🔎</button>
              </div>
            </div>
          </div>

          <div className={`${s.cartList} ${s.panel}`}>
            {basketView.length === 0 && <div className={`${s.empty} ${s.small}`}>O‘tkazilayotgan tovarlar yo‘q</div>}
            {basketView.map((b)=> (
              <BasketRow
                key={b.id}
                b={b}
                onUnit={editUnit}
                onQtyDelta={changeQtyDelta}
                onQtyInput={editQty}
                onRemove={removeRow}
              />
            ))}
          </div>

          <div className={`${s.panel} fr-card`}>
            <div className={s.label}>Barcha tovar narxiga qo‘shish</div>
            <div className={s.adder}>
              <div className={s.grp}>
                <input
                  className={s.input}
                  type="text"
                  inputMode="decimal"
                  value={extraUsd}
                  onChange={(e)=>setExtraUsd(normDecStr(e.target.value))}
                  onBlur={(e)=>setExtraUsd(normDecStr(e.target.value))}
                  placeholder="so'm"
                />
                <span className={s.suf}>so'm</span>
              </div>

              <div className={s.grp}>
                <input
                  className={s.input}
                  type="text"
                  inputMode="decimal"
                  value={extraPct}
                  onChange={(e)=>setExtraPct(normDecStr(e.target.value))}
                  onBlur={(e)=>setExtraPct(normDecStr(e.target.value))}
                  placeholder="%"
                />
                <span className={s.suf}>%</span>
              </div>

              <div className={s.totals}>
                <div>Tovarlar soni: <b>{fmtQty(totalQty)}</b></div>
                <div>Jami: <b>{money(totalSum)}</b></div>
              </div>
            </div>

            <button className={s.primary} disabled={!canTransfer} onClick={doTransfer}>
              {submitting ? "O‘tkazilmoqda…" : "O‘tkazish"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= Cards & Rows ================= */
const ProductCard = React.memo(function ProductCard({ p, onAdd }) {
  const out = p.qty <= 0;
  const handle = useCallback(() => { if (!out) onAdd(p); }, [out, onAdd, p]);

  const don = p.prices["donaga"];
  const opt = p.prices["optom"];
  const vip = p.prices["vip klient"] ?? p.prices["vip"] ?? undefined;

  return (
    <div
      className={s.pCard}
      onClick={handle}
      title={!out ? "O‘tkazish ro‘yxatiga qo‘shish" : "Stok yo‘q"}
      style={{ position: "relative", contain: "content" }}
    >
      {out && <span className={s.oosTag}>Stok yo‘q</span>}

      <div className={s.imgPh}>
        {p.rasm_url ? (
          <img className={s.pImg} src={p.rasm_url} alt="" loading="lazy" decoding="async" width="160" height="160" />
        ) : <div style={{ width: "100%", height: "100%" }} />}
      </div>

      <div className={s.pMeta}>
        <div className={s.pName}>{p.name}</div>
        <div className={s.pSku}>{p.sku}</div>
        <div className={s.pStock}>{fmtQty(p.qty)} dona</div>
      </div>

      <div className={s.pPrice}>
        <div>Tan: {money(p.buy)}</div>
        {typeof opt !== "undefined" && <div>Optom: {money(opt)}</div>}
        {typeof don !== "undefined" && <div>Donaga: {money(don)}</div>}
        {typeof vip !== "undefined" && <div>VIP: {money(vip)}</div>}
      </div>
    </div>
  );
});

const BasketRow = React.memo(function BasketRow({ b, onUnit, onQtyDelta, onQtyInput, onRemove }) {
  return (
    <div className={s.cartRow}>
      <div className={s.cTit}>
        <div className={s.cNm}>{b.name}</div>
        <div className={s.cSku}>{b.sku}</div>
      </div>

      <div className={s.unitBox}>
        <input
          className={`${s.input} ${s.inputTight}`}
          type="text"
          inputMode="decimal"
          value={b.unitUsd}
          onChange={(e) => onUnit(b.id, e.target.value)}
          onBlur={(e) => onUnit(b.id, normDecStr(e.target.value))}
          title="Birlik narxi (so'm)"
        />
        <small>so'm</small>
      </div>

      <div className={s.qtyBox}>
        <button className={s.cartBtn} onClick={() => onQtyDelta(b.id, -1)}>–</button>
        <input
          className={`${s.input} ${s.inputTight}`}
          style={{ minWidth: 90 }}
          type="text"
          inputMode="decimal"
          value={b.qty}
          onChange={(e) => onQtyInput(b.id, e.target.value)}
          onBlur={(e) => onQtyInput(b.id, normDecStr(e.target.value))}
          title="Miqdor"
        />
        <button className={s.cartBtn} onClick={() => onQtyDelta(b.id, +1)}>+</button>
      </div>

      <div className={s.cartSum}>{money(toNum(b.unitAdj) * toNum(b.qty))}</div>
      <button className={s.cartRemove} onClick={() => onRemove(b.id)} title="Olib tashlash">×</button>
    </div>
  );
});
